import {CaregiverQueryServiceClient} from 'gabi-api-ts/v2/caregiver/query/caregiver_query.client';
import {PatientQueryServiceClient} from 'gabi-api-ts/v2/patient/query/patient_query.client';
import PropTypes from 'prop-types';
import React from 'react';
import autoBind from 'react-autobind';
import {withTranslation} from 'react-i18next';

import {PatientCaregiverForm} from '@/components/business/common/patient/patient-caregiver-form';
import LoadingView from '@/components/static/loading-view';
import {withBackendQuery} from '@/decorators/with-backend-query';
import {useBackendQuery} from '@/hooks/use-backend-query';
import {BackendApiRefreshListeners} from '@/services/backend-api-refresh-listeners';
import {BackendApiService} from '@/services/backend-api-service';
import {FeedbackAlertsService, FeedbackAlertType} from '@/services/feedback-alerts-service';

function withCaregiver(Component) {
    return withBackendQuery('caregiverDataLoading', 'caregiverData', 'caregiverDataError', props => useBackendQuery({
        serviceClient: CaregiverQueryServiceClient,
        query: CaregiverQueryServiceClient.prototype.getPersonalDetails,
        memoize: false,
        disabled: !(props.patient?.caregiverInformation?.id),
        data: props.patient?.caregiverInformation?.id,
    }))(Component);
}

class PatientUpdateCaregiver extends React.Component {
    static propTypes = {
        className: PropTypes.string,
        t: PropTypes.func,
        caregiverDataLoading: PropTypes.bool,
        caregiverData: PropTypes.object,
        patient: PropTypes.object.isRequired,
        onChanged: PropTypes.func,
    };

    state = {
        loading: false,
        caregiverId: null,
    };
    
    get caregiverExists() {
        return !!this.props.caregiverData;
    }

    constructor(props) {
        super(props);
        autoBind(this);
    }

    render() {
        const props = this.props;
        const state = this.state;
        const { t } = this.props;

        return (
            <div className={`patient-update-caregiver ${props.className ?? ''}`}>
                <h3>{t('updateCaregiver.title') ?? 'Caregiver information'}</h3>

                {!state.loading && !props.caregiverDataLoading &&
                    <PatientCaregiverForm
                        caregiver={props.caregiverData}
                        disableLanguageEdition={this.caregiverExists}
                        loading={state.loading}
                        patient={props.patient}
                        onChanged={this.handleFormChanged}
                        onSubmit={(caregiver) => this.setPatientCaregiver(caregiver)}
                    />
                }

                {state.loading &&
                    <LoadingView />
                }
            </div>
        );
    }

    handleFormChanged(notSavedDataCaregiver) {
        if(this.props.onChanged) {
            this.props.onChanged(notSavedDataCaregiver);
        }
    }

    setPatientCaregiver(caregiver) {
        if (this.caregiverExists) {
            this.updatePatientCaregiver(caregiver);
        }
        else {
            this.createPatientCaregiver(caregiver);
        }
    }

    updatePatientCaregiver(caregiver) {
        this.setState(state => ({
            ...state,
            loading: true,
            caregiver: caregiver,
        }));

        BackendApiService.getRequest({
            domain: 'caregiver',
            modelName: 'updateCaregiver',
            data: {
                caregiverId: this.props.patient.caregiverInformation.id.id,
                caregiver: caregiver
            }
        }).then(() => {
            FeedbackAlertsService.add(FeedbackAlertType.SUCCESS, 'updateCaregiver', 'Caregiver updated!');
            BackendApiRefreshListeners.refreshByCall(CaregiverQueryServiceClient, CaregiverQueryServiceClient.prototype.getPersonalDetails);
            BackendApiRefreshListeners.refreshByCall(PatientQueryServiceClient, PatientQueryServiceClient.prototype.get);
            this.setState(state => ({
                ...state,
                loading: false,
            }));
            return caregiver;
        }).catch(err => {
            console.error(err);
            this.setState(state => ({
                ...state,
                loading: false,
            }));
        });
    }

    createPatientCaregiver(caregiver) {
        this.setState(state => ({
            ...state,
            loading: true,
            caregiver: caregiver,
        }));

        BackendApiService.getRequest({
            domain: 'caregiver',
            modelName: 'createCaregiver',
            data: {
                caregiver: caregiver
            }
        }).then((caregiver) => BackendApiService.getRequest({
            domain: 'patient',
            modelName: 'assignCaregiverToPatient',
            data: {
                patientId: this.props.patient.patientId.id,
                caregiverId: caregiver.id,
            }
        })).then(async (caregiver) => {
            // Wait a few seconds for the changes to be propagated in the backend
            await new Promise((r) => setTimeout(r, 2000));
            return caregiver;
        }).then((caregiver) => {
            FeedbackAlertsService.add(FeedbackAlertType.SUCCESS, 'updateCaregiver', 'Caregiver created!');
            BackendApiRefreshListeners.refreshByCall(PatientQueryServiceClient, PatientQueryServiceClient.prototype.get);
            BackendApiRefreshListeners.refreshByCall(CaregiverQueryServiceClient, CaregiverQueryServiceClient.prototype.getPersonalDetails);
            this.setState(state => ({
                ...state,
                loading: false,
            }));
            return caregiver;
        }).catch(err => {
            console.error(err);
            this.setState(state => ({
                ...state,
                loading: false,
            }));
        });
    }
}

PatientUpdateCaregiver = withTranslation()(withCaregiver(PatientUpdateCaregiver));

export {PatientUpdateCaregiver};
